/* Kioni beaded fringe (password page): strands of beads hang from a gold bar on each side of
   the screen and point in towards the message. Beads are drawn exactly like the 404 loom.
   Brushing past them (cursor or finger) sends a ripple down the strands; they swing back.
   Strands always stop short of the message, logo and footer, so on narrow screens they
   shrink or disappear. With reduced motion the fringe is drawn once and stays still. */
(() => {
  if (customElements.get('kx-bead-fringe')) return;

  const PALETTES = {
    brand: ['#43362E', '#C49F36', '#706E61', '#2E3547', '#5F3F46', '#070101', '#4A4F53'],
    'gold-ink': ['#070101', '#C49F36', '#1C1617', '#D3B874'],
    'navy-wine': ['#2E3547', '#5F3F46', '#434B62', '#C49F36', '#7B5E63'],
    earth: ['#43362E', '#706E61', '#C49F36', '#5C4C42', '#969389']
  };
  const GOLD = '#C49F36';
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const parseRgb = (str) => (str.match(/[\d.]+/g) || [234, 233, 228]).slice(0, 3).map(Number);
  const lum = (hex) => { const n = parseInt(hex.slice(1), 16); return ((n >> 16) * 0.299 + ((n >> 8) & 255) * 0.587 + (n & 255) * 0.114) / 255; };
  const tri = (t) => 1 - Math.abs(((t % 2) + 2) % 2 - 1); // 0 → 1 → 0 over a period of 2
  const rrect = (g, x, y, w, h, r) => {
    g.beginPath();
    g.moveTo(x + r, y);
    g.arcTo(x + w, y, x + w, y + h, r);
    g.arcTo(x + w, y + h, x, y + h, r);
    g.arcTo(x, y + h, x, y, r);
    g.arcTo(x, y, x + w, y, r);
    g.closePath();
  };

  class KxBeadFringe extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.canvas = this.querySelector('canvas');
      if (!this.canvas) return;
      this.ready = true;
      this.ctx = this.canvas.getContext('2d');
      this.root = this.closest('[data-kx-pw]') || document.body;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.interactive = this.dataset.interactive !== 'false' && !this.reduced;
      this.palette = PALETTES[this.dataset.palette] || PALETTES.brand;
      this.size = clamp(parseInt(this.dataset.size, 10) || 14, 8, 24);
      this.raf = 0;
      this.prev = null;

      const relayout = () => { this.layout(); this.draw(performance.now()); };
      this.ro = new ResizeObserver(relayout);
      this.ro.observe(this.root);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(relayout);
      relayout();

      if (this.reduced) return;
      if (this.interactive) {
        const local = (e) => {
          const r = this.canvas.getBoundingClientRect();
          return { x: e.clientX - r.left, y: e.clientY - r.top, t: performance.now() };
        };
        this.onMove = (e) => { const p = local(e); this.brush(this.prev || p, p, false); this.prev = p; };
        this.onDown = (e) => { const p = local(e); this.brush(p, p, true); this.prev = p; };
        this.onLeave = () => { this.prev = null; };
        this.root.addEventListener('pointermove', this.onMove, { passive: true });
        this.root.addEventListener('pointerdown', this.onDown, { passive: true });
        this.root.addEventListener('pointerleave', this.onLeave);
        this.root.addEventListener('pointercancel', this.onLeave);
      }
      this.loop = (now) => { this.step(now); this.draw(now); this.raf = requestAnimationFrame(this.loop); };
      this.raf = requestAnimationFrame(this.loop);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      if (this.ro) this.ro.disconnect();
      if (this.onMove) {
        this.root.removeEventListener('pointermove', this.onMove);
        this.root.removeEventListener('pointerdown', this.onDown);
        this.root.removeEventListener('pointerleave', this.onLeave);
        this.root.removeEventListener('pointercancel', this.onLeave);
      }
      this.ready = false;
    }

    /* ---------- Layout: one strand per row on each side, sized to the free space ---------- */
    layout() {
      const box = this.root.getBoundingClientRect();
      const W = Math.round(box.width), H = Math.round(box.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const s = W < 1100 ? Math.max(9, Math.round(this.size * 0.8)) : this.size;
      Object.assign(this, { W, H, s, dpr });
      this.canvas.width = Math.round(W * dpr);
      this.canvas.height = Math.round(H * dpr);
      this.canvas.style.width = `${W}px`;
      this.canvas.style.height = `${H}px`;

      // Colours that sit on the page background (dark beads turn cream on dark themes)
      const bg = parseRgb(getComputedStyle(this.root).backgroundColor);
      const light = (bg[0] * 0.299 + bg[1] * 0.587 + bg[2] * 0.114) / 255 > 0.5;
      this.thread = light ? 'rgba(7,1,1,.22)' : 'rgba(234,233,228,.24)';
      this.paints = this.palette.map((c) => (!light && lum(c) < 0.25 ? '#EAE9E4' : c));
      this.sprites = {};

      // Everything the fringe must stay clear of
      const keep = [...this.root.querySelectorAll('[data-fringe-avoid]')]
        .map((el) => el.getBoundingClientRect())
        .filter((r) => r.width && r.height)
        .map((r) => ({ l: r.left - box.left, r: r.right - box.left, t: r.top - box.top, b: r.bottom - box.top }));
      const top = s * 2.4; // just below the colour line; the logo is kept clear like the rest
      const foot = this.root.querySelector('[data-fringe-floor]');
      const bottom = foot ? foot.getBoundingClientRect().top - box.top - s * 1.2 : H - s * 2;
      const pitch = s * 2.05; // space between strands so each reads as its own string
      const rows = Math.max(0, Math.floor((bottom - top) / pitch));
      const gap = Math.max(44, s * 3.4); // breathing room between the tips and the text
      const barInset = Math.max(10, s * 0.9);
      const maxBeads = Math.floor(Math.min(W * 0.2, 300) / s);

      this.barInset = barInset;
      this.top = top;
      this.bottom = top + rows * pitch;
      this.strands = [];
      // Phones: no room beside the text, so no fringe at all rather than stubs in the gaps
      if (W < 600) return;

      ['left', 'right'].forEach((side) => {
        const dir = side === 'left' ? 1 : -1;
        const x0 = side === 'left' ? barInset : W - barInset;
        // 1. For every row: the chevron length it would like, and how many beads fit before the text
        const plan = [];
        for (let i = 0; i < rows; i++) {
          const y = top + i * pitch + pitch / 2;
          let room = W / 2 - barInset - gap;
          keep.forEach((k) => {
            if (y < k.t - s || y > k.b + s) return;
            const d = side === 'left' ? k.l - x0 : x0 - k.r;
            room = Math.min(room, d - gap);
          });
          const shape = 0.34 + 0.66 * tri(i / 5);
          plan.push({ i, y, wanted: Math.max(3, maxBeads * shape), fits: Math.floor(room / s) - 1 });
        }
        // 2. Scale the whole side down evenly so no strand is chopped against the text.
        //    Rows that would force it below 60% are left out instead (a clean gap, not a stub).
        let rowsIn = plan.filter((r) => r.fits >= 3);
        let scale = 1;
        for (;;) {
          scale = Math.min(1, ...rowsIn.map((r) => r.fits / r.wanted));
          if (scale >= 0.6 || !rowsIn.length) break;
          rowsIn = rowsIn.filter((r) => r.fits / r.wanted >= 0.6);
        }
        rowsIn.forEach(({ i, y, wanted, fits }) => {
          const n = Math.min(fits, Math.max(3, Math.round(wanted * scale)));
          const band = Math.abs((i % 6) - 3);
          const beads = [];
          for (let j = 0; j < n; j++) {
            const color = this.paints[Math.floor((j + band) / 2) % this.paints.length];
            beads.push({ color, dy: 0, vy: 0 });
          }
          this.strands.push({ side, dir, x0, y, i, beads });
        });
      });
    }

    sprite(color) {
      if (this.sprites[color]) return this.sprites[color];
      const { s, dpr } = this;
      const cv = document.createElement('canvas');
      cv.width = cv.height = Math.ceil(s * dpr);
      const g = cv.getContext('2d');
      g.scale(dpr, dpr);
      const w = s * 0.88, h = s * 0.8, x = (s - w) / 2, y = (s - h) / 2;
      rrect(g, x, y, w, h, s * 0.24);
      g.fillStyle = color;
      g.fill();
      g.globalCompositeOperation = 'source-atop';
      const grad = g.createLinearGradient(0, y, 0, y + h);
      grad.addColorStop(0, 'rgba(255,255,255,.32)');
      grad.addColorStop(0.45, 'rgba(255,255,255,0)');
      grad.addColorStop(1, 'rgba(0,0,0,.24)');
      g.fillStyle = grad;
      g.fillRect(0, 0, s, s);
      g.fillStyle = 'rgba(255,255,255,.4)';
      g.beginPath();
      g.ellipse(x + w * 0.3, y + h * 0.27, s * 0.11, s * 0.05, -0.2, 0, Math.PI * 2);
      g.fill();
      return (this.sprites[color] = cv);
    }

    // The round gold drop at the end of each strand
    drop() {
      if (this.sprites.drop) return this.sprites.drop;
      const { s, dpr } = this;
      const cv = document.createElement('canvas');
      cv.width = cv.height = Math.ceil(s * dpr);
      const g = cv.getContext('2d');
      g.scale(dpr, dpr);
      const r = s * 0.34;
      g.beginPath();
      g.arc(s / 2, s / 2, r, 0, Math.PI * 2);
      const grad = g.createRadialGradient(s * 0.38, s * 0.36, r * 0.1, s / 2, s / 2, r);
      grad.addColorStop(0, '#E9D08A');
      grad.addColorStop(0.55, GOLD);
      grad.addColorStop(1, '#8E7022');
      g.fillStyle = grad;
      g.fill();
      return (this.sprites.drop = cv);
    }

    /* ---------- Interaction ---------- */
    brush(a, b, tap) {
      const s = this.s;
      const dt = Math.max(8, b.t - a.t) / 16.667;
      const pvy = clamp((b.y - a.y) / dt, -s * 0.9, s * 0.9);
      const reach = s * (tap ? 2.6 : 1.9);
      for (const st of this.strands) {
        if (Math.abs(st.y - b.y) > reach + s) continue;
        st.beads.forEach((bead, j) => {
          const bx = st.x0 + st.dir * (j + 0.5) * s;
          const by = st.y + bead.dy;
          // distance from the bead to the pointer's path
          const dx = bx - b.x, dy = by - b.y;
          const d = Math.hypot(dx, dy);
          if (d > reach) return;
          const f = 1 - d / reach;
          const away = (dy === 0 ? (Math.random() < 0.5 ? -1 : 1) : Math.sign(dy)) * s * (tap ? 0.5 : 0.16);
          bead.vy += f * (pvy * 0.22 + away);
        });
      }
    }

    // A simple rope: each bead is pulled towards its neighbours and gently back to rest
    step(now) {
      const s = this.s;
      const t = now / 1000;
      for (const st of this.strands) {
        const B = st.beads, n = B.length;
        for (let j = 0; j < n; j++) {
          const b = B[j];
          const left = j === 0 ? 0 : B[j - 1].dy;
          const right = j === n - 1 ? b.dy : B[j + 1].dy;
          const anchor = j === 0 ? 0.3 : 0;
          const breeze = Math.sin(t * 0.9 + st.i * 0.42 + j * 0.3) * 0.0016 * s * (j / n);
          b.vy += 0.16 * (left + right - 2 * b.dy) - (0.006 + anchor) * b.dy + breeze;
          b.vy *= 0.94;
        }
        for (const b of B) b.dy = clamp(b.dy + b.vy, -s * 1.3, s * 1.3);
      }
    }

    /* ---------- Drawing ---------- */
    draw() {
      const { ctx, dpr, s, W, H } = this;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      if (!this.strands.length) return;

      // The two gold bars the strands hang from
      ctx.fillStyle = GOLD;
      const barW = Math.max(3, s * 0.26);
      ['left', 'right'].forEach((side) => {
        if (!this.strands.some((st) => st.side === side)) return;
        const x = side === 'left' ? this.barInset - barW : this.W - this.barInset;
        rrect(ctx, x, this.top - s * 0.4, barW, this.bottom - this.top + s * 0.8, barW / 2);
        ctx.fill();
      });

      ctx.strokeStyle = this.thread;
      ctx.lineWidth = Math.max(1, s * 0.06);
      for (const st of this.strands) {
        const B = st.beads, n = B.length;
        const px = (j) => st.x0 + st.dir * (j + 0.5) * s;
        // thread
        ctx.beginPath();
        ctx.moveTo(st.x0, st.y);
        for (let j = 0; j < n; j++) ctx.lineTo(px(j), st.y + B[j].dy);
        ctx.lineTo(px(n - 1) + st.dir * s * 0.7, st.y + B[n - 1].dy);
        ctx.stroke();
        // beads, tilted with the thread
        for (let j = 0; j < n; j++) {
          const prev = j === 0 ? 0 : B[j - 1].dy, next = j === n - 1 ? B[j].dy : B[j + 1].dy;
          const a = Math.atan2((next - prev) / 2, s) * st.dir;
          const x = px(j), y = st.y + B[j].dy;
          ctx.save();
          ctx.translate(x, y);
          if (a) ctx.rotate(a);
          ctx.drawImage(this.sprite(B[j].color), -s / 2, -s / 2, s, s);
          ctx.restore();
        }
        // gold drop at the tip
        const tx = px(n - 1) + st.dir * s * 0.88, ty = st.y + B[n - 1].dy;
        ctx.drawImage(this.drop(), tx - s / 2, ty - s / 2, s, s);
      }
    }
  }

  customElements.define('kx-bead-fringe', KxBeadFringe);
})();
